import '../../features/Sales/SalesDashboardCarousel/SalesDashboardCarousel.css'
import { Accordion, Badge, Button, Center, Flex, Grid, Loader, Modal, Select, Stack, TextInput } from "@mantine/core";
import { useContext, useMemo, useState } from "react";
import AppContext from '../../contexts/AppContext/AppContext'
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { BarChart } from '@mantine/charts';
import moment from "moment/moment";
import { IconCalendar } from "@tabler/icons-react";
import { useFetch } from "@mantine/hooks";
import AuthContext from "../../contexts/AuthContext";

const MONTHS = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

const monthNameToIndex = {
    enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
    julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11,
    // Abreviaciones comunes
    ene: 0, feb: 1, mar: 2, abr: 3, may: 4, jun: 5,
    jul: 6, ago: 7, sep: 8, sept: 8, oct: 9, nov: 10, dic: 11
};

const normalize = (str = "") =>
    str
        .toLowerCase()
        .normalize("NFD") // separa diacríticos
        .replace(/[\u0300-\u036f]/g, "") // quita diacríticos
        .trim();

function parseSpanishDateToJSDate(input) {
    if (!input || typeof input !== "string") return null;
    const s = normalize(input);

    // Regex mejorada para capturar formatos como:
    // "10 de septiembre de 2025", "10 DE SEPTIEMBRE DE 2025", "10 septiembre 2025", etc.
    const regex = /(\d{1,2})\s+(?:de\s+)?([a-zñáéíóú]+)\s+(?:de\s+|del\s+)?(\d{4})/i;
    const match = s.match(regex);

    if (!match) return null;

    const day = parseInt(match[1], 10);
    const monthStr = match[2];
    const year = parseInt(match[3], 10);

    // Buscar el índice del mes
    const monthIndex = monthNameToIndex[monthStr];

    if (monthIndex === undefined) {
        // Intentar con los primeros 3 caracteres si no coincide exactamente
        const monthKey = Object.keys(monthNameToIndex).find(
            key => key.startsWith(monthStr.slice(0, 3))
        );
        if (!monthKey) return null;
        const idx = monthNameToIndex[monthKey];
        if (idx === undefined) return null;

        const d = new Date(year, idx, day);
        if (Number.isNaN(d.getTime())) return null;
        return d;
    }

    // Construir la fecha
    const d = new Date(year, monthIndex, day);
    if (Number.isNaN(d.getTime())) return null;
    return d;
}

const chart_colors = [
    "#A8C5E2", // azul pastel
    "#C3D9E6", // azul grisáceo suave
    "#B8E0D2", // verde agua suave
    "#D7E8BA", // verde pastel
    "#F2D5A9", // durazno suave
    "#E8C1C5", // rosa desaturado
    "#D4A5A5", // rosa viejo
    "#C3B9DD", // lila suave
    "#B0A8B9", // lavanda grisácea
    "#D8CAB8", // beige gris suave
    "#E2D4C0", // topo claro
    "#B4CDE1", // azul suave
    "#C6E2E9", // celeste apagado
    "#F1D4E5", // rosa muy suave
    "#F3E9D2", // crema suave
    "#D6D1B1", // oliva claro gris
    "#C3D4C3", // verde gris
    "#B8CACF", // celeste piedra
    "#DAC9E9", // lila desaturado
    "#F0D2C2", // coral suave
    "#E0B8B8", // rosado frío
    "#D6B5D8", // púrpura pastel
    "#C8D3E8", // azul cielo apagado
    "#BDD6C4", // verde menta apagado
    "#E7DCC8"  // arena suave
];

export default function SalesDashboardAcademicUnit() {
    const { specialties: specialtiesOptions } = useContext(AppContext);
    const { width } = useWindowDimensions();
    const isMobile = width <= 768;

    const { authToken } = useContext(AuthContext)
    const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/dashboard-academicUnit`;
    const config = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
    };
    const { data: dashboardData, loading } = useFetch(
        url, config
    );

    const [isLeadModal, setIsLeadModal] = useState(false)

    const getTargetProgress = ({ equalTo100, target }) => {
        if (!target || target === 0) return 0;
        return (equalTo100 / target) * 100;
    };

    const [filters, setFilters] = useState({
        month: MONTHS[moment().month()],
        year: moment().year().toString(),
        type: '',
        specialty: '',
        search: '',
    });

    const filteredAcademicUnits = useMemo(() => {
        if (!dashboardData?.academicUnits?.length) return [];

        const monthIndex = MONTHS.indexOf(filters.month);

        return dashboardData.academicUnits.filter(item => {
            if (!item?.initialDate) return false;

            // Filtro por tipo
            if (filters.type) {
                if (item?.type.toLowerCase() !== filters?.type.toLowerCase()) return false;
            }

            // Filtro por especialidad
            if (filters.specialty) {
                const hasSpecialty = item.specialties?.some(s => s._id === filters.specialty);
                if (!hasSpecialty) return false;
            }

            // Filtro por búsqueda
            if (filters.search) {
                if (!item?.name.toUpperCase().includes(filters?.search.toUpperCase()))
                    return false;
            }

            // Parsear la fecha una sola vez
            const parsed = parseSpanishDateToJSDate(item.initialDate);
            let dateToUse = parsed;

            if (!parsed) {
                // Fallback con moment
                const normalized = normalize(item.initialDate);
                const m = moment(normalized, "D [de] MMMM [del] YYYY", true);

                if (m.isValid()) {
                    dateToUse = m.toDate();
                } else {
                    console.warn("No se pudo parsear initialDate:", item.initialDate);
                    return false;
                }
            }

            // Validar año SIEMPRE (independiente del mes)
            if (dateToUse.getFullYear().toString() !== filters.year) {
                return false;
            }

            // Validar mes SOLO si se seleccionó un mes específico
            if (filters.month && filters.month !== '') {
                if (dateToUse.getMonth() !== monthIndex) {
                    return false;
                }
            }

            return true;
        });
    }, [dashboardData, filters]);

    const [chartFilters, setChartFilters] = useState({
        type: "average",
        order: 'asc'
    })

    const chartData = useMemo(() => {
        const data = dashboardData?.averageAgeBySpecialty || [];

        return data.map((item, index) => ({
            specialtyName: item.specialtyName,
            averageAge: item.averageAge || 0,
            count: item.count || 0,
            color: chart_colors[index % chart_colors.length]
        })).sort((a, b) => {
            const type = chartFilters.type === 'average' ? 'averageAge' : "count";
            return chartFilters.order === 'asc'
                ? a[type] - b[type]
                : b[type] - a[type];
        });
    }, [dashboardData, chartFilters]);

    if (loading) return <Center>
        <Stack align='center'>
            <Loader />
            <h3>Cargando ventas...</h3>
        </Stack>
    </Center>

    return <>
        <h3 className="salesDashboardCarousel-header">Estadísticas de Ventas:</h3>
        <Grid gap={8}>
            <Grid.Col span={{ base: 6, sm: 3 }}>
                <Select
                    label='Año'
                    allowDeselect={false}
                    value={filters.year}
                    onChange={(value) => {
                        if (!value) return;
                        setFilters(prev => ({ ...prev, year: value }));
                    }}
                    data={[moment().year().toString(), (moment().year() - 1).toString()]}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 6, sm: 3 }}>
                <Select
                    label='Mes'
                    allowDeselect={false}
                    value={filters.month}
                    onChange={(value) => {

                        setFilters(prev => ({ ...prev, month: value }));
                    }}
                    data={[{ value: '', label: "Todos" }, ...MONTHS]}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>

                <Select
                    allowDeselect={false}
                    label='Especialidad'
                    value={filters.specialty}
                    onChange={(value) => {
                        setFilters(prev => ({ ...prev, specialty: value || '' }));
                    }}
                    data={[{ value: '', label: 'Todas' }, ...specialtiesOptions]}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>

                <Select
                    allowDeselect={false}
                    label='Tipo de unidad academica'
                    value={filters.type}
                    onChange={(value) => {
                        setFilters(prev => ({ ...prev, type: value || '' }));
                    }}
                    data={[
                        { value: '', label: 'Todos' },
                        { value: 'curso', label: 'Curso' },
                        { value: 'diplomado', label: 'Diplomado' },
                        { value: 'posgrado', label: 'Posgrado' },
                        { value: 'webinar', label: 'Webinar' },
                        { value: 'masterclass', label: 'Masterclass' },
                        { value: 'taller', label: 'Taller' },
                        { value: 'bootcamp', label: 'Bootcamp' },
                    ]}
                />
            </Grid.Col>

        </Grid>
        <TextInput
            label='Buscar por nombre'
            onChange={({ target }) => {
                if (!target.value) return
                const search = target.value.trim()
                setFilters(prev => ({ ...prev, search }))
            }}
        />

        <Stack gap={0} classNames={{
            root: "salesDashboardCarousel--table-container"

        }}>
            <Grid styles={{
                root: {
                    padding: '.8rem'
                }
            }}>
                <Grid.Col span={{ base: 3, md: 4 }}>
                    <h5 className="salesDashboardCarousel-stats_table-headline">Unidad academica</h5>
                </Grid.Col>
                <Grid.Col span={{ base: 1, md: 2 }} >
                    <Flex align={'center'} justify={'center'} h='100%'>
                        <h5 className="salesDashboardCarousel-stats_table-headline">
                            Objetivo
                        </h5>
                    </Flex>
                </Grid.Col>
                <Grid.Col span={2}>
                    <Flex align={'center'} justify={'center'} h='100%'>

                        <h5 className="salesDashboardCarousel-stats_table-headline">Inscriptos al 100%</h5>
                    </Flex>
                </Grid.Col>
                <Grid.Col span={2}>
                    <Flex align={'center'} justify={'center'} h='100%'>
                        <h5 className="salesDashboardCarousel-stats_table-headline">Total de inscrip.</h5>
                    </Flex>
                </Grid.Col>
                <Grid.Col span={{ base: 2, md: 1 }}>
                    <Flex align={'center'} justify={'center'} h='100%'>
                        <h5 className="salesDashboardCarousel-stats_table-headline">Estado</h5>
                    </Flex>
                </Grid.Col>
                <Grid.Col span={{ base: 2, md: 1 }}>
                    <Flex align={'center'} justify={'center'} h='100%'>
                        <h5 className="salesDashboardCarousel-stats_table-headline">Prospectos</h5>
                    </Flex>
                </Grid.Col>
            </Grid>

            {filteredAcademicUnits.length === 0 && (
                <div style={{ padding: "1rem", opacity: 0.7 }}><p className="salesDashboardCarousel-stats_table-headline">No se encontraron unidades para {filters.month}</p></div>
            )}

            {filteredAcademicUnits.map((item, idx) => {
                const progress = getTargetProgress({ equalTo100: item.equalTo100, target: item.inscription_target });
                return <Grid key={item._id} classNames={{ root: `${"salesDashboardCarousel-stats_table--row"} ${idx % 2 === 0 ? "salesDashboardCarousel-stats_table--row-bg-color" : ""}` }}>
                    <Grid.Col span={{ base: 3, md: 4 }}>
                        <Flex align={'center'} justify={'start'} h='100%'>
                            <span className="salesDashboardCarousel-stats_table-content">
                                {item.name}
                            </span>
                        </Flex>
                    </Grid.Col>
                    <Grid.Col span={{ base: 1, md: 2 }} >
                        <Flex align={'center'} justify={'center'} h='100%'>
                            <span className="salesDashboardCarousel-stats_table-content">
                                {item.inscription_target}
                            </span>
                        </Flex>
                    </Grid.Col>
                    <Grid.Col span={2}>
                        <Flex align={'center'} justify={'center'} h='100%'>
                            <span className="salesDashboardCarousel-stats_table-content">
                                {item.equalTo100}
                            </span>
                        </Flex>
                    </Grid.Col>
                    <Grid.Col span={2}>
                        <Flex align={'center'} justify={'center'} h='100%'>
                            <span className="salesDashboardCarousel-stats_table-content">
                                {item.paymentsCount}
                            </span>
                        </Flex>
                    </Grid.Col>
                    <Grid.Col span={{ base: 2, md: 1 }}>
                        <Flex align={'center'} justify={'center'} h='100%'>

                            <Badge bg={progress > 100 ? "var(--color-green)" : progress > 70 ? "var(--color-yellow)" : "var(--color-red)"}>{`${progress.toFixed(0)}%`}</Badge>
                        </Flex>
                    </Grid.Col>
                    <Grid.Col span={{ base: 2, md: 1 }}>
                        {item?.leads?.count > 0 ? <Button type="button" variant="outline" w='100%' onClick={() => setIsLeadModal(item._id)}>
                            {item.leads?.count}
                        </Button>
                            : <Flex align={'center'} justify={'center'} h='100%'>
                                <span className="salesDashboardCarousel-nolead-span">{item.leads?.count}</span>
                            </Flex>
                        }
                    </Grid.Col>
                </Grid>
            })}
        </Stack>

        {
            isLeadModal && <LeadModal isLeadModal={isLeadModal} onClose={() => setIsLeadModal(null)} />
        }

        {
            chartData && (
                <div className="salesDashboardCarousel-chart-wrapper">
                    <h3 className="salesDashboardCarousel-header">Promedio de edad según especialidades:</h3>

                    <Flex gap={12}>
                        <Select
                            allowDeselect={false}
                            defaultValue={chartFilters.type}
                            data={[
                                { value: 'average', label: "Promedio de edad" },
                                { value: 'total', label: "Total de inscriptos" }
                            ]}
                            onChange={(value) => setChartFilters(prev => ({ ...prev, type: value }))}
                            label='Tipo'
                        />
                        <Select
                            allowDeselect={false}
                            defaultValue={chartFilters.order}
                            data={[
                                { value: 'asc', label: "De menor a mayor" },
                                { value: 'dsc', label: "De mayor a menor" },
                            ]}
                            onChange={(value) => setChartFilters(prev => ({ ...prev, order: value }))}
                            label='Orden'
                        />
                    </Flex>

                    <Grid my={'1rem'}>
                        <Grid.Col span='auto'>
                            <h5 className="salesDashboardCarousel-chartHeadline">Promedio</h5>
                            <BarChart
                                h={600}
                                w='350px'
                                data={chartData}
                                dataKey="specialtyName"
                                orientation="vertical"
                                withBarValueLabel
                                yAxisProps={{ width: 240 }} barChartProps={{ syncId: 'average' }}
                                series={[
                                    {
                                        label: "Promedio de edad",
                                        name: "averageAge",
                                        color: "blue.6", // Este color es fallback (no importa)
                                    },
                                ]}
                            />
                        </Grid.Col>
                        {!isMobile && <div style={{

                            height: 600,
                            width: '2px',
                            background: "#000",
                            margin: "1rem",
                            opacity: .2
                        }} />}
                        <Grid.Col span='auto'>
                            <h5 className="salesDashboardCarousel-chartHeadline">{isMobile ? "Total" : "Total de inscriptos"}</h5>
                            <BarChart
                                h={600}
                                w={isMobile ? "100px" : "350px"}
                                data={chartData}
                                dataKey="specialtyName"
                                orientation="vertical"
                                withBarValueLabel
                                yAxisProps={{ width: 0 }} barChartProps={{ syncId: 'average' }}
                                series={[
                                    {
                                        label: "Total",
                                        name: "count",
                                        color: "blue.6", // Este color es fallback (no importa)
                                    },
                                ]}
                            />
                        </Grid.Col>
                    </Grid>
                </div>
            )
        }

        {/* <div className="salesDashboardCarousel-splide-div">
                <h4 className="salesDashboardCarousel-splide-header">Alumnos por curso:</h4>
                <Splide
                    options={{
                        perPage: 3,
                        perMove: 1,
                        gap: "1rem",
                        breakpoints: {
                            1200: { perPage: 2 },
                            801: { perPage: 1 }
                        }
                    }}
                >
                    {dashboardData?.courses?.map((item, index) => (
                        <SplideSlide key={index}>
                            <SalesDashboardStats item={item} />
                        </SplideSlide>
                    ))}
                </Splide>
            </div> */}

    </>
}



const LeadModal = ({ isLeadModal, onClose }) => {
    const { width } = useWindowDimensions();
    const isMobile = width <= 768;

    const { authToken } = useContext(AuthContext)
    const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/leads?unitId=${isLeadModal}`;
    const config = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
    };
    const { data, loading } = useFetch(
        url, config
    );



    return <Modal
        size={isMobile ? 'lg' : '70vw'}
        opened={isLeadModal}
        onClose={onClose}
    >
        <h3 className="SalesDashboardCarousel-Lead-modal-title">Prospectos</h3>
        {loading ? <Center>
            <Stack align='center' gap={0}>
                <Loader type='dots' />
                <h3>Cargando...</h3>
            </Stack>
        </Center> : <Stack>
            <Grid >
                <Grid.Col span={3}>
                    <h4 className="SalesDashboardCarousel-LeadModal-table-headline">Nombre</h4>
                </Grid.Col>
                <Grid.Col span={3}>
                    <h4 className="SalesDashboardCarousel-LeadModal-table-headline">Email</h4>
                </Grid.Col>
                <Grid.Col span={3}>
                    <h4 className="SalesDashboardCarousel-LeadModal-table-headline">Teléfono</h4>
                </Grid.Col>
                <Grid.Col span={3}>
                    <h4 className="SalesDashboardCarousel-LeadModal-table-headline">Estado</h4>
                </Grid.Col>
            </Grid>
            {data?.map(lead => {

                const STATUS = lead?.contactAttempts.length
                    ? lead?.contactAttempts.sort((a, b) => moment(b.date).diff(a.date))[0]?.outcome
                    : null

                return <Stack key={lead._id} styles={{
                    root: {
                        backgroundColor: '#fff',
                        boxShadow: "1px 1px 4px #000000b6",
                        borderRadius: '4px',
                    }
                }}>
                    <Grid styles={{
                        root: {
                            padding: ".5rem"
                        }
                    }}>
                        <Grid.Col span={3}>
                            <h4 className="SalesDashboardCarousel-LeadModal-table-headline">{lead.name}</h4>
                        </Grid.Col>
                        <Grid.Col span={3}>

                            <p className="SalesDashboardCarousel-LeadModal-table-headline">{lead.email}</p>

                        </Grid.Col>
                        <Grid.Col span={3}>
                            <p className="SalesDashboardCarousel-LeadModal-table-headline">{lead.phone}</p>
                        </Grid.Col>
                        <Grid.Col span={3}>
                            {STATUS ? <Badge color={STATUS === 'PENDING' ? "#FFCB47" : STATUS === 'SUCCESS' ? "#71cb5f" : '#DC3D3D'}>
                                <span>{STATUS === 'PENDING' ? "PENDIENTE" : STATUS === 'SUCCESS' ? "EXITOSO" : 'FALLIDO'}</span>
                            </Badge>
                                : <Badge variant="outline">Sin interraciones</Badge>
                            }
                        </Grid.Col>
                    </Grid>
                    {lead?.contactAttempts?.length > 0 && <Accordion
                        styles={{
                            root: {
                                background: "#bebebe1b"
                            }
                        }}
                    >
                        <Accordion.Item value='contact'>
                            <Accordion.Control>
                                <h4 className="SalesDashboardCarousel-LeadModal-table-headline">Todas las interacciones</h4>
                            </Accordion.Control>
                            <Accordion.Panel>{lead?.contactAttempts?.map((item, idx) => (
                                <Flex key={idx}>
                                    <Flex style={{ flexGrow: 1 }} gap={12}>
                                        <Flex gap={4} align='center'>
                                            <IconCalendar />
                                            <span className="SalesDashboardCarousel-LeadModal-table-headline">{moment(item.date).format('DD/MM/YYYY')}</span>
                                        </Flex>
                                        <Flex gap={4} align='center'>-</Flex>
                                        <Flex gap={4} align='center'>
                                            <span className="SalesDashboardCarousel-LeadModal-table-headline">{item.method}</span>
                                        </Flex>
                                    </Flex>
                                    <Badge color={item.outcome === 'PENDING' ? "#FFCB47" : item.outcome === 'SUCCESS' ? "#71cb5f" : '#DC3D3D'}>
                                        <span>{item.outcome === 'PENDING' ? "PENDIENTE" : item.outcome === 'SUCCESS' ? "EXITOSO" : 'FALLIDO'}</span>
                                    </Badge>
                                </Flex>
                            ))}
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                    }
                </Stack>
            })}
        </Stack>}
        <Button my={12} type="button" onClick={onClose}>Cerrar</Button>
    </Modal>
}